import { Alert, Box, DateRange, PrimaryButton, Select, Text } from "@nulogy/components";
import FieldRow from "components/common/FieldRow";
import ModalFooter from "components/common/ModalFooter";
import Form from "components/controls/Form";
import NDSDatePicker from "components/controls/NDSDatePicker";
import { MAX_WORK_ORDER_COUNT } from "components/pages/schedule/publish/publishConstants";
import WorkOrder from "domain/WorkOrder";
import Codenames from "policies/Codenames";
import React, { useState } from "react";
import DateTime from "utils/DateTime";
import StringUtils from "utils/StringUtils";
import ExpandFiltersButton from "./ExpandFiltersButton";

const FORM_ID = "update-work-order-dates-modal";

const initialStartDate = new Date();
initialStartDate.setHours(0, 0, 0, 0);
const initialValues = { startDate: initialStartDate };

export default function PublishWorkOrderDatesFormView({ onSave, response, ...contentProps }) {
  const formErrors = response.errors.length ? response.errors : null;

  return (
    <Form errors={formErrors} id={FORM_ID} initialValues={initialValues} onSave={onSave}>
      {({ isSubmitting }) => <Content isSubmitting={isSubmitting} response={response} {...contentProps} />}
    </Form>
  );
}

export function Content({
  customers,
  workOrders,
  isSubmitting,
  onFilterChange,
  filters,
  showWorkOrderCount,
  workOrderCountTotal,
  workOrderCountOnInactiveLines,
  response,
}) {
  const workOrderLimitExceeded = workOrderCountTotal > MAX_WORK_ORDER_COUNT;
  const submitEnabled = !response.success && workOrderCountTotal > 0 && !workOrderLimitExceeded && filters.startDate;
  const minDate = DateTime.timeAgo("2", "weeks");

  const [expandedFilters, setExpandedFilters] = useState(false);

  const customerOptions = customers.map((customer) => ({ label: customer, value: customer }));

  const handleStartDateChange = ({ value: startDate }) => {
    onFilterChange({
      startDate,
    });
  };

  const handleCustomersToUpdateChange = (updatedCustomers) => {
    onFilterChange({
      customers: updatedCustomers,
    });
  };

  const workOrderCodeOptions = workOrders.map((workOrder) => ({ label: workOrder.code, value: workOrder.code }));
  const handleCodesToUpdateChange = (codes) => {
    onFilterChange({
      codes,
    });
  };

  const { OPEN, BOOKED } = WorkOrder.STATUS_TYPES;
  const workOrderStatusOptions = [
    { label: "Open", value: OPEN },
    { label: "Booked", value: BOOKED },
  ];
  const handleStatusToUpdateChange = (statuses) => {
    onFilterChange({
      statuses,
    });
  };

  const handleDueDateRangeChange = (value) => {
    const { startDate: dueStartDate, endDate: dueEndDate } = value;
    onFilterChange({
      dueDateFrom: dueStartDate,
      dueDateTo: dueEndDate,
    });
  };

  const toggleExpandedFilters = () => {
    setExpandedFilters(!expandedFilters);
  };

  return (
    <>
      {response.success && (
        <Alert type="success">
          You successfully updated {workOrderCountTotal} {StringUtils.pluralize("work order", workOrderCountTotal)}
        </Alert>
      )}
      <Box marginBottom="x2">
        <Text>
          Work order planned start and end dates will be updated in {Codenames.PackmanagerShort} based on this schedule.
        </Text>
      </Box>
      <FieldRow>
        <NDSDatePicker
          errorMessage={
            workOrderLimitExceeded
              ? `Only ${MAX_WORK_ORDER_COUNT} work orders can be updated at a time. Please reduce the selected date range and try again.`
              : null
          }
          field="startDate"
          inputProps={{ labelText: "Update From" }}
          minDate={minDate}
          onChange={handleStartDateChange}
        />
      </FieldRow>
      {expandedFilters && (
        <>
          <FieldRow>
            <Select
              closeMenuOnSelect={false}
              labelText="Customer(s)"
              maxHeight="250"
              multiselect
              onChange={handleCustomersToUpdateChange}
              options={customerOptions}
              requirementText="(Optional)"
              value={filters.customers}
            />
          </FieldRow>
          <FieldRow>
            <Select
              closeMenuOnSelect={false}
              labelText="Work order code(s)"
              maxHeight="250"
              multiselect
              onChange={handleCodesToUpdateChange}
              options={workOrderCodeOptions}
              requirementText="(Optional)"
              value={filters.codes}
            />
          </FieldRow>
          <FieldRow>
            <Select
              closeMenuOnSelect={false}
              labelText="Work order status(es)"
              maxHeight="250"
              multiselect
              onChange={handleStatusToUpdateChange}
              options={workOrderStatusOptions}
              requirementText="(Optional)"
              value={filters.statuses}
            />
          </FieldRow>
          <FieldRow>
            <DateRange
              defaultEndDate={filters.dueDateTo}
              defaultStartDate={filters.dueDateFrom}
              labelProps={{ labelText: "Due date range", requirementText: "(Optional)" }}
              onRangeChange={handleDueDateRangeChange}
            />
          </FieldRow>
        </>
      )}
      <ExpandFiltersButton expandedFilters={expandedFilters} onClick={toggleExpandedFilters} />
      {workOrderCountOnInactiveLines > 0 && (
        <Alert style={{ marginBottom: "16px" }} type="warning">
          This date range contains blocks scheduled on inactive lines. Their work order planned start and end dates will
          be updated, unless they are unscheduled.
        </Alert>
      )}
      <ModalFooter alignItems="center" justifyContent="flex-start">
        <Box marginRight="x2">
          <PrimaryButton disabled={!submitEnabled} form={FORM_ID} type="submit">
            Update
          </PrimaryButton>
        </Box>
        {!isSubmitting && showWorkOrderCount && (
          <Text className="spec-update-work-order-dates-modal-work-order-count-message">
            {workOrderCountTotal} {StringUtils.pluralize("work order", workOrderCountTotal)} will be updated.{" "}
          </Text>
        )}
      </ModalFooter>
    </>
  );
}
